/**
 * @typedef {Object} CarnetDetails
 * @property {string} master_id - The master organization ID
 * @property {Date|string} creation_date - Date when the carnet was created
 * @property {boolean} isSoldToParticular - Whether sold to a particular client
 * @property {boolean} isSoldToProfessional - Whether sold to the first client
 * @property {Array<string>} partners_id - Array of partner organization IDs with access
 */

/**
 * Checks if an organization has access to view a carnet.
 * An organization has access if they are:
 * 1. The master organization that created the carnet
 * 2. Listed in the carnet's visions
 * 3. Listed in the carnet's partners_id array
 * @param {Object} orgData - The organization data
 * @param {Object} carnetDetails - The carnet details
 * @returns {boolean} - Whether the organization has access
 */
const hasAccessToCarnet = (orgData, carnetDetails) => {
  if (!orgData || !carnetDetails) return false;
  // if (!(["wmLGIwRMtQD2LLc6nXQf", "se4um6hWlIVKiLxln3gB"].includes(orgData.id) || orgData.partner_type === "expert" && ["wmLGIwRMtQD2LLc6nXQf", "se4um6hWlIVKiLxln3gB"].includes(orgData.master_id))) return false;
  if (orgData.id === carnetDetails?.master_id) return true;
  if (
    carnetDetails.visions &&
    carnetDetails.visions.some((vision) => vision.org_id === orgData.id)
  )
    return true;
  if (
    carnetDetails.partners_id &&
    carnetDetails.partners_id.includes(orgData.id)
  )
    return true;
  return false;
};

const folderHasCarnet = (folder) => {
  if (!folder) return false;
  return folder.carnet_id != null;
};

/**
 * Determines if an organization can create a carnet for a folder.
 * Only specific authorized master organizations can create carnets,
 * and only if one doesn't already exist.
 * @param {Object} orgData - The organization data
 * @param {string} orgData.id - The organization ID
 * @param {Object} folder - The folder data
 * @param {string} folder.master_id - The master organization ID that owns the folder
 * @param {string} [folder.carnet_id] - The carnet ID if one exists
 * @returns {boolean} - Whether the organization can create a carnet
 */
const canCreateCarnet = (orgData, folder) => {
  if (!orgData?.id || !folder?.master_id) return false;

  // Cannot create a carnet if one already exists
  if (folderHasCarnet(folder)) return false;

  // Only specific authorized organizations can create carnets
  const AUTHORIZED_ORG_IDS = ["wmLGIwRMtQD2LLc6nXQf", "se4um6hWlIVKiLxln3gB"];
  return AUTHORIZED_ORG_IDS.includes(folder.master_id);
};

/**
 * Fetches carnet details for a specific folder.
 * @param {string} folderId - The folder ID.
 * @param {string} token - The user's auth token.
 * @param {string} prefix - The API base URL.
 * @returns {Promise<Object>} - The response containing carnet details.
 */
const getCarnetDetails = async (folderId, token, prefix) => {
  const url = `${prefix}folders/${folderId}/carnet_details`;
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    return await response.json();
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export {
  hasAccessToCarnet,
  canCreateCarnet,
  folderHasCarnet,
  getCarnetDetails,
};
